import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckIcon, DownloadIcon, FileTextIcon, StarIcon } from 'lucide-react';

import { Button } from '@/components/new-ui/button';
import { Card } from '@/components/new-ui/card';
import { env } from '@/constants/env';
import { getEstateQueryOptions } from '@/features/estates-property/api/estate';
import { useUpdateEstateThumbnail } from '@/features/estates-property/api/estate-thumbnail';
import { useUpdateProjectThumbnail } from '@/features/project-media/api/update-thumbnail';
import { getProjectQueryOptions } from '@/features/projects/api/projects';
import { cn } from '@/lib/utils';
import { formatDate } from '@/utils/formatDate';

import { ACCEPTED_MEDIA_TYPES } from '../schema/upload-media';

export type MediaItem = {
  id: string;
  mediaId: string | null;
  name?: string | null;
  fileName?: string | null;
  fileType?: string | null;
  createdAt?: string | null;
  isUsedByFlatfinder?: boolean;
  // set for estate owned items, project media merged into an estate page has none
  estateId?: string | null;
  projectId?: string | null;
};

export const getMediaItemName = (file: MediaItem) => file.name ?? file.fileName ?? '';

export const getMediaItemFileType = (file: MediaItem) =>
  typeof file.fileType === 'string' ? file.fileType : '';

type MediaFileOwner = {
  id?: string | null;
  mediaId?: string | null;
  estateId?: string | null;
  projectId?: string | null;
};

// Floor plans may be PDFs stored outside the images bucket, so they are served by the layouts
// route of whoever owns the file (an estate page also lists its project's floor plans)
export const getMediaFileUrl = (file: MediaFileOwner, isFloorPlan: boolean) => {
  const key = file.mediaId || file.id;
  let path = 'images';
  if (isFloorPlan && file.estateId) path = `estates/${file.estateId}/layouts`;
  else if (isFloorPlan && file.projectId) path = `projects/${file.projectId}/layouts`;
  return `${env.API_URL}/${path}/${key}?preview=1`;
};

const SELECT_BOX_BASE =
  'flex h-5 w-5 shrink-0 items-center justify-center rounded border shadow-sm';

// Square checkbox from the design, used on cards and on the date group headings
export const MediaSelectBox = ({
  checked,
  className,
}: {
  checked: boolean;
  className?: string;
}) => (
  <span
    aria-hidden="true"
    className={cn(
      SELECT_BOX_BASE,
      checked
        ? 'border-primary bg-primary text-nu-primary-foreground'
        : 'border-border bg-background/90',
      className
    )}
  >
    {checked ? <CheckIcon className="h-3.5 w-3.5" /> : null}
  </span>
);

type UseMediaItemActionsArgs = {
  type: 'estate' | 'project';
  id: string;
  page: string;
};

// Single item actions shown on card hover: download and set as thumbnail
export const useMediaItemActions = ({ type, id, page }: UseMediaItemActionsArgs) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // The current thumbnail is marked on its card. Both queries are already cached by the page layout
  const { data: estate } = useQuery({ ...getEstateQueryOptions(id), enabled: type === 'estate' });
  const { data: project } = useQuery({
    ...getProjectQueryOptions(id),
    enabled: type === 'project',
  });
  const thumbnailId = (type === 'project' ? project?.thumbnailId : estate?.thumbnailId) ?? null;

  const { mutate: updateEstateThumbnail } = useUpdateEstateThumbnail({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('update-estate-details-success'));
        queryClient.invalidateQueries({ queryKey: getEstateQueryOptions(id).queryKey });
      },
    },
  });

  const { mutate: updateProjectThumbnail } = useUpdateProjectThumbnail({
    mutationConfig: {
      onSuccess: () => {
        toast.success(t('update-project-success'));
        queryClient.invalidateQueries({ queryKey: getProjectQueryOptions(id).queryKey });
      },
    },
  });

  const setThumbnail = (file: MediaItem) => {
    if (type === 'project') {
      updateProjectThumbnail({ projectId: id, imageId: file.id });
    } else {
      updateEstateThumbnail({ estateId: id, imageId: file.id });
    }
  };

  const download = (file: MediaItem) => {
    // same source as the preview carousel download
    fetch(getMediaFileUrl(file, page === 'floor-plans'), { credentials: 'include' })
      .then((response) => {
        if (!response.ok) throw new Error(`Download failed with ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = getMediaItemName(file) || 'image';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        toast.error(t('somethingWentWrong'));
      });
  };

  return { setThumbnail, download, thumbnailId };
};

type MediaItemCardProps = {
  file: MediaItem;
  isSelecting: boolean;
  isSelected: boolean;
  onClick: () => void;
  onDownload: () => void;
  // only passed for writers on pages where an item can become the thumbnail
  onSetThumbnail?: () => void;
  /** This item is the current thumbnail: its star stays visible without hover */
  isThumbnail?: boolean;
};

// Card actions show on hover, on keyboard focus and always on touch screens
const HOVER_ONLY_CLASS =
  'h-7 w-7 shadow-sm opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100';

export const MediaItemCard = ({
  file,
  isSelecting,
  isSelected,
  onClick,
  onDownload,
  onSetThumbnail,
  isThumbnail = false,
}: MediaItemCardProps) => {
  const { t } = useTranslation();
  const name = getMediaItemName(file);
  const isDisabled = isSelecting && Boolean(file.isUsedByFlatfinder);

  return (
    <Card
      className={cn(
        'group relative cursor-pointer overflow-hidden transition-shadow',
        isSelecting && isSelected && 'border-primary ring-2 ring-primary',
        isDisabled && 'cursor-not-allowed opacity-60'
      )}
      title={isDisabled ? t('imageInUse') : undefined}
    >
      <button
        type="button"
        className="block w-full text-left disabled:cursor-not-allowed"
        disabled={isDisabled}
        aria-pressed={isSelecting ? isSelected : undefined}
        onClick={onClick}
      >
        <div className="relative aspect-square">
          {getMediaItemFileType(file) === ACCEPTED_MEDIA_TYPES.Pdf ? (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <FileTextIcon className="h-8 w-8 text-muted-foreground" />
            </div>
          ) : (
            <img
              src={`${env.API_URL}/images/${file.mediaId}`}
              alt={name}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          )}
          {isSelecting ? (
            <MediaSelectBox checked={isSelected} className="absolute left-2 top-2" />
          ) : null}
        </div>
        <div className="p-2">
          <p className="truncate text-xs text-muted-foreground" title={name}>
            {name}
          </p>
          <p className="whitespace-nowrap text-xs text-muted-foreground/70">
            {file.createdAt ? formatDate(file.createdAt) : ''}
          </p>
        </div>
      </button>
      {isSelecting ? null : (
        <div className="absolute right-2 top-2 flex gap-1">
          <Button
            size="icon"
            variant="secondary"
            className={HOVER_ONLY_CLASS}
            title={t('download')}
            aria-label={t('download')}
            onClick={onDownload}
          >
            <DownloadIcon className="h-3.5 w-3.5" />
          </Button>
          {isThumbnail ? (
            // The current thumbnail is marked all the time, also for readers
            <span
              className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-secondary text-secondary-foreground shadow-sm"
              title={t('thumbnail')}
            >
              <StarIcon className="h-3.5 w-3.5 fill-current" aria-label={t('thumbnail')} />
            </span>
          ) : onSetThumbnail ? (
            <Button
              size="icon"
              variant="secondary"
              className={HOVER_ONLY_CLASS}
              title={t('setAsThumbnail')}
              aria-label={t('setAsThumbnail')}
              onClick={onSetThumbnail}
            >
              <StarIcon className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      )}
    </Card>
  );
};

type MediaDateGroupProps = {
  date: string;
  items: MediaItem[];
  isSelecting: boolean;
  isItemSelected: (file: MediaItem) => boolean;
  onItemClick: (file: MediaItem) => void;
  onItemDownload: (file: MediaItem) => void;
  onItemSetThumbnail?: (file: MediaItem) => void;
  // limits set as thumbnail to some items, for example estate owned images only
  canItemSetThumbnail?: (file: MediaItem) => boolean;
  /** Id of the image that is the current thumbnail */
  thumbnailId?: string | null;
};

type MediaSelectionBarProps = {
  selectedCount: number;
  isAllSelected: boolean;
  onToggleAll: () => void;
};

// Select mode row above the cards: how many are selected, then select all
export const MediaSelectionBar = ({
  selectedCount,
  isAllSelected,
  onToggleAll,
}: MediaSelectionBarProps) => {
  const { t } = useTranslation();

  return (
    <div className="new-ui mt-2 flex items-center gap-4">
      <span className="text-sm tabular-nums text-muted-foreground">
        {t('mediaSelectedCount', { count: selectedCount })}
      </span>
      <button
        type="button"
        onClick={onToggleAll}
        aria-pressed={isAllSelected}
        className="inline-flex items-center gap-2 text-sm font-medium text-foreground"
      >
        <MediaSelectBox checked={isAllSelected} />
        {t('select-all')}
      </button>
    </div>
  );
};

// One upload date: small heading (select all while selecting) and the item grid
export const MediaDateGroup = ({
  date,
  items,
  isSelecting,
  isItemSelected,
  onItemClick,
  onItemDownload,
  onItemSetThumbnail,
  canItemSetThumbnail,
  thumbnailId,
}: MediaDateGroupProps) => (
  <div className="new-ui flex flex-col gap-2">
    {/* Select all lives in the selection row above the groups */}
    <h3 className="text-base font-semibold text-foreground">{formatDate(date, 'de-DE')}</h3>
    <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,140px),1fr))] gap-4">
      {items.map((file) => (
        <MediaItemCard
          key={file.id}
          file={file}
          isSelecting={isSelecting}
          isSelected={isItemSelected(file)}
          onClick={() => onItemClick(file)}
          onDownload={() => onItemDownload(file)}
          onSetThumbnail={
            onItemSetThumbnail && (!canItemSetThumbnail || canItemSetThumbnail(file))
              ? () => onItemSetThumbnail(file)
              : undefined
          }
          isThumbnail={Boolean(thumbnailId) && file.id === thumbnailId}
        />
      ))}
    </div>
  </div>
);
